import React from 'react';
import { cn } from './cn';
import { R, T } from './tokens';

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'danger-solid' | 'warning-solid';
type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'full';

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary:       'bg-primary-500 text-white hover:bg-primary-600 shadow-sm',
  secondary:     'bg-slate-100 text-slate-700 hover:bg-slate-200',
  danger:        'text-red-600 border border-red-200 hover:bg-red-50',
  ghost:         'text-slate-500 hover:bg-slate-100',
  'danger-solid':  'bg-red-500 text-white hover:bg-red-600 shadow-lg shadow-red-500/20',
  'warning-solid': 'bg-amber-500 text-white hover:bg-amber-600 shadow-lg shadow-amber-500/20',
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  xs:   'px-2.5 py-1 text-xs gap-1',
  sm:   'px-3 py-2 text-sm md:px-3 md:py-1.5 md:text-xs gap-1.5',
  md:   'px-4 py-2.5 text-sm gap-2',
  lg:   'px-5 py-3 text-sm gap-2',
  full: 'w-full py-2.5 text-sm gap-2 justify-center',
};

const Spinner = () => (
  <svg
    className="animate-spin h-4 w-4 shrink-0"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
    />
  </svg>
);

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: React.ReactNode;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      icon,
      disabled,
      className,
      children,
      ...rest
    },
    ref,
  ) => {
    const isDisabled = disabled || loading;

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        className={cn(
          // base
          'inline-flex items-center font-medium cursor-pointer',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          'active:scale-[0.97]',
          R.base,
          T.fast,
          'min-h-[44px] md:min-h-[32px] touch-manipulation',
          // variant + size
          VARIANT_CLASSES[variant],
          SIZE_CLASSES[size],
          className,
        )}
        {...rest}
      >
        {loading ? <Spinner /> : icon ? <span className="shrink-0">{icon}</span> : null}
        {children}
      </button>
    );
  },
);

Button.displayName = 'Button';
